// ============================================================================
// offlineTar.js — a SAJÁT offline pillanatkép-tár (2026-10-23, docs/OFFLINE_TERV_2026-10-22.md, V1a).
//
// MIÉRT SAJÁT TÁR: a Firestore SDK tartós gyorsítótára nem szűrhető szobánként (minden olvasott dokumentumot eltárol, nyílt szöveggel), ezért a
// "ez a szoba nem menthető offline" szabály vele nem lenne igaz. Az SDK ezért CSAK memóriában gyorsítótáraz; az offline ENGEDÉLYEZETT szobák
// tartalmát ez a modul menti egy saját, verziózott IndexedDB-tárba, felhasználónként, saját korláttal és élettartammal.
//
// A modul két részből áll: tiszta logika (szerializálás, takarítási terv - Node-ban tesztelhető) és a tár (`OfflineTar`), amely egy egyszerű
// háttértárat (`{ get, put, delete, list }`) kap: a böngészőben az IndexedDB-burkolót (`idbHatter`), a tesztben a memória-háttértárat.
//
// Mit tárol: szobalista (csak az engedélyezett szobák), szobánként a témák listája, témánként a legutóbbi hozzászólások. Képet NEM (V1).
// Korlátok: összesen MAX_BAJT, rekordonként MAX_KOR_MS élettartam, LRU (a legrégebben használt rekord esik ki először).
// Kijelentkezéskor / tagság megszűnésekor célzott törlés; a mentés a SZERVER által visszaigazolt pillanatképből történik (offlineKezelo.js).
// ============================================================================

export const OFFLINE_ADATBAZIS = "lineage0-offline";
export const OFFLINE_SEMA_VERZIO = 1;
export const MAX_BAJT = 20 * 1024 * 1024;
export const MAX_KOR_MS = 14 * 24 * 60 * 60 * 1000;
export const MAX_UZENET_TEMANKENT = 50;
export const MAX_TEMA_SZOBANKENT = 100;

// ---------------------------------------------------------------------------
// Szerializálás: a Firestore időbélyegek (toMillis / toDate) nem menthetők közvetlenül - egyszerű alakra írjuk, és visszaállításkor ugyanazt
// a felületet (toMillis, toDate, seconds) adó objektumot építünk belőlük, hogy a megjelenítés ne tudjon a különbségről.
// ---------------------------------------------------------------------------

function idobelyegAlak(ms) {
  return {
    toMillis: () => ms,
    toDate: () => new Date(ms),
    seconds: Math.floor(ms / 1000),
    nanoseconds: (ms % 1000) * 1e6,
  };
}

export function szerializal(ertek) {
  if (ertek === null || ertek === undefined) return null;
  if (typeof ertek === "function") return undefined;
  if (typeof ertek !== "object") return ertek;
  if (typeof ertek.toMillis === "function") {
    try {
      return { __ts: ertek.toMillis() };
    } catch (e) {
      return null;
    }
  }
  if (Array.isArray(ertek)) return ertek.map((x) => { const v = szerializal(x); return v === undefined ? null : v; });
  if (ertek instanceof Set) return { __set: [...ertek].map(szerializal) };
  const ki = {};
  for (const [k, v] of Object.entries(ertek)) {
    if (v === undefined) continue;
    const s = szerializal(v);
    if (s !== undefined) ki[k] = s;
  }
  return ki;
}

export function visszaallit(ertek) {
  if (ertek === null || typeof ertek !== "object") return ertek;
  if (Array.isArray(ertek)) return ertek.map(visszaallit);
  if (typeof ertek.__ts === "number" && Object.keys(ertek).length === 1) return idobelyegAlak(ertek.__ts);
  if (Array.isArray(ertek.__set) && Object.keys(ertek).length === 1) return new Set(ertek.__set.map(visszaallit));
  const ki = {};
  for (const [k, v] of Object.entries(ertek)) ki[k] = visszaallit(v);
  return ki;
}

/** A rekord becsült mérete bájtban (a JSON UTF-8 hossza). */
export function bajtMeret(ertek) {
  try {
    const s = JSON.stringify(ertek);
    return typeof TextEncoder !== "undefined" ? new TextEncoder().encode(s).length : s.length * 2;
  } catch (e) {
    return 0;
  }
}

export const rekordKulcs = (uid, tipus, azon) => `${uid}|${tipus}|${azon}`;

/**
 * Takarítási terv (tiszta): mely rekordok törlendők? Először a lejártak; ha az összeg még így is a korlát fölött van, a legrégebben használtak
 * (usedAt), amíg a korlát alá nem kerülünk. A rekordok: { key, bytes, savedAt, usedAt }.
 * @returns {{ torlendo: string[], marad: number, bajt: number }}
 */
export function takaritasiTerv(rekordok, most, { maxBajt = MAX_BAJT, maxKorMs = MAX_KOR_MS } = {}) {
  const torlendo = [];
  const el = [];
  for (const r of rekordok) {
    if (typeof r.savedAt === "number" && most - r.savedAt > maxKorMs) torlendo.push(r.key);
    else el.push(r);
  }
  let osszeg = el.reduce((s, r) => s + (r.bytes || 0), 0);
  if (osszeg > maxBajt) {
    const rendezett = [...el].sort((a, b) => (a.usedAt || 0) - (b.usedAt || 0));
    for (const r of rendezett) {
      if (osszeg <= maxBajt) break;
      torlendo.push(r.key);
      osszeg -= r.bytes || 0;
    }
  }
  const torolt = new Set(torlendo);
  return { torlendo, marad: el.filter((r) => !torolt.has(r.key)).length, bajt: osszeg };
}

// ---------------------------------------------------------------------------
// Háttértárak
// ---------------------------------------------------------------------------

/** Memória-háttértár (tesztekhez és IndexedDB nélküli környezethez: a tartalom a lap bezárásával elvész). */
export function memoriaHatter() {
  const m = new Map();
  return {
    async get(k) { return m.has(k) ? JSON.parse(JSON.stringify(m.get(k))) : undefined; },
    async put(k, v) { m.set(k, JSON.parse(JSON.stringify(v))); },
    async delete(k) { m.delete(k); },
    async list() { return [...m.values()].map((v) => JSON.parse(JSON.stringify(v))); },
    async clear() { m.clear(); },
  };
}

/** IndexedDB-háttértár: egyetlen objektumtár ("rekordok", kulcs: key). Hiba esetén a hívó memória-háttértárra eshet vissza. */
export function idbHatter(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  const idb = win.indexedDB;
  if (!idb) throw new Error("nincs IndexedDB");
  let nyitas = null;
  const nyit = () => {
    if (nyitas) return nyitas;
    nyitas = new Promise((ok, hiba) => {
      const kerés = idb.open(OFFLINE_ADATBAZIS, OFFLINE_SEMA_VERZIO);
      kerés.onupgradeneeded = () => {
        const db = kerés.result;
        if (!db.objectStoreNames.contains("rekordok")) db.createObjectStore("rekordok", { keyPath: "key" });
      };
      kerés.onsuccess = () => ok(kerés.result);
      kerés.onerror = () => hiba(kerés.error);
      kerés.onblocked = () => hiba(new Error("az offline tár megnyitását egy másik fül blokkolja"));
    });
    return nyitas;
  };
  const kerese = (mod, fn) =>
    nyit().then((db) => new Promise((ok, hiba) => {
      const tx = db.transaction("rekordok", mod);
      const tar = tx.objectStore("rekordok");
      const kerés = fn(tar);
      tx.oncomplete = () => ok(kerés && "result" in kerés ? kerés.result : undefined);
      tx.onerror = () => hiba(tx.error);
      tx.onabort = () => hiba(tx.error);
    }));
  return {
    get: (k) => kerese("readonly", (t) => t.get(k)),
    put: (k, v) => kerese("readwrite", (t) => t.put(v)),
    delete: (k) => kerese("readwrite", (t) => t.delete(k)),
    list: () => kerese("readonly", (t) => t.getAll()),
    clear: () => kerese("readwrite", (t) => t.clear()),
  };
}

// ---------------------------------------------------------------------------
// A tár
// ---------------------------------------------------------------------------

export class OfflineTar {
  /** @param {{get,put,delete,list,clear}} hatter */
  constructor(hatter, { maxBajt = MAX_BAJT, maxKorMs = MAX_KOR_MS } = {}) {
    this.hatter = hatter;
    this.maxBajt = maxBajt;
    this.maxKorMs = maxKorMs;
  }

  /** Egy rekord mentése (felhasználónként). A roomId a szobánkénti célzott törléshez kell. */
  async ment(uid, tipus, azon, adat, { roomId = null, most = Date.now() } = {}) {
    const key = rekordKulcs(uid, tipus, azon);
    const tarolt = szerializal(adat);
    const rekord = { key, uid, tipus, azon, roomId, savedAt: most, usedAt: most, bytes: bajtMeret(tarolt), adat: tarolt };
    await this.hatter.put(key, rekord);
    await this.takarit(most);
    return rekord.bytes;
  }

  /** Olvasás: lejárt rekordot nem ad (és töröl); az olvasás frissíti az LRU-sorrendet. @returns {{adat, savedAt}|null} */
  async olvas(uid, tipus, azon, most = Date.now()) {
    const key = rekordKulcs(uid, tipus, azon);
    const r = await this.hatter.get(key);
    if (!r || r.uid !== uid) return null;
    if (typeof r.savedAt === "number" && most - r.savedAt > this.maxKorMs) {
      await this.hatter.delete(key);
      return null;
    }
    r.usedAt = most;
    await this.hatter.put(key, r);
    return { adat: visszaallit(r.adat), savedAt: r.savedAt };
  }

  async torol(uid, tipus, azon) {
    await this.hatter.delete(rekordKulcs(uid, tipus, azon));
  }

  /** Egy szoba MINDEN mentett rekordja törlődik (tagság megszűnése, a szoba offline tiltása). */
  async torolSzoba(uid, roomId) {
    const mind = await this.hatter.list();
    for (const r of mind) if (r.uid === uid && r.roomId === roomId) await this.hatter.delete(r.key);
  }

  /** Egy felhasználó MINDEN rekordja törlődik (kijelentkezés). */
  async torolMindent(uid) {
    const mind = await this.hatter.list();
    for (const r of mind) if (r.uid === uid) await this.hatter.delete(r.key);
  }

  /** MINDEN felhasználó rekordja törlődik (közös eszközre váltás, a régi tár ürítése). */
  async torolMindenkiet() {
    await this.hatter.clear();
  }

  async takarit(most = Date.now()) {
    const mind = await this.hatter.list();
    const terv = takaritasiTerv(mind, most, { maxBajt: this.maxBajt, maxKorMs: this.maxKorMs });
    for (const k of terv.torlendo) await this.hatter.delete(k);
    return terv;
  }

  /** Statisztika a felhasználónak (profilsor): rekordok száma, bájt, legrégebbi mentés. */
  async statisztika(uid) {
    const mind = (await this.hatter.list()).filter((r) => r.uid === uid);
    return {
      rekord: mind.length,
      bajt: mind.reduce((s, r) => s + (r.bytes || 0), 0),
      legregebbiMentes: mind.reduce((m, r) => (m === null || r.savedAt < m ? r.savedAt : m), null),
      legutobbiMentes: mind.reduce((m, r) => (m === null || r.savedAt > m ? r.savedAt : m), null),
    };
  }
}
